import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Target, ShieldCheck, TrendingUp, Scale } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const SCREENS = [
  {
    icon: Target, accent: "text-tc-coach",
    title: "WELCOME TO TRADECRAFT",
    body: "Learn the market. Master the craft. This is a training ground for disciplined trading — no real money, no risk to you.",
  },
  {
    icon: TrendingUp, accent: "text-tc-mastery",
    title: "YOUR JOB IS NOT TO PREDICT THE FUTURE",
    body: "Your job is to make disciplined decisions under uncertainty. Nobody knows what the next candle will do. Good traders manage that uncertainty.",
  },
  {
    icon: Scale, accent: "text-tc-xp",
    title: "PROCESS > PROFIT",
    body: "Good trades sometimes lose. Bad trades sometimes win. One trade proves very little. We score how you think, not how much you made.",
  },
  {
    icon: ShieldCheck, accent: "text-tc-bull",
    title: "PROTECT CAPITAL",
    body: "You start with a $10,000 simulated account and a 1% default risk. That means a maximum planned risk of just $100 per trade. Capital preservation comes first.",
  },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { refreshUser, user } = useAuth();
  const [i, setI] = useState(0);
  const last = i === SCREENS.length - 1;
  const S = SCREENS[i];

  const finish = async () => {
    try { await api.post("/auth/onboard", { onboarded: true }); await refreshUser(); } catch (e) {}
    const pending = localStorage.getItem("tc_pending_invite");
    navigate(pending ? `/family?invite=${encodeURIComponent(pending)}` : "/dashboard");
  };

  if (user && user.role !== "student") { navigate("/parent"); return null; }

  return (
    <div className="min-h-screen bg-tc-bg tc-grid-bg flex items-center justify-center p-6">
      <div className="w-full max-w-xl">
        <div className="flex gap-2 mb-8 justify-center">
          {SCREENS.map((_, idx) => (
            <div key={idx} className={`h-1.5 rounded-full transition-all ${idx <= i ? "w-10 bg-tc-coach" : "w-6 bg-white/10"}`} />
          ))}
        </div>
        <div key={i} className="tc-card p-8 sm:p-12 text-center animate-fade-up">
          <div className={`w-16 h-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mx-auto mb-6 ${S.accent}`}>
            <S.icon className="w-8 h-8" />
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">{S.title}</h1>
          <p className="text-tc-sub mt-4 leading-relaxed">{S.body}</p>
          <div className="mt-9 flex items-center justify-between">
            <button data-testid="onboarding-skip" onClick={finish} className="text-sm text-tc-muted hover:text-tc-sub">Skip</button>
            {last ? (
              <button data-testid="onboarding-start" onClick={finish}
                className="flex items-center gap-2 rounded-lg bg-tc-bull text-tc-bg font-semibold px-6 py-2.5 hover:bg-tc-bull/90 transition-colors">
                Start Training <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button data-testid="onboarding-next" onClick={() => setI(i + 1)}
                className="flex items-center gap-2 rounded-lg bg-tc-coach text-tc-bg font-semibold px-6 py-2.5 hover:bg-tc-coach/90 transition-colors">
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
